// pendingGrounding — a nascent conversation's pre-send grounding as ONE pure,
// `ConversationId`-keyed map (hero-is-initial-conversation-state §3.3).
//
// A nascent conversation has an anchor (its client-minted id) but NO durable
// aggregate server-side yet, so neither `introduceToContext` NOR
// `groundConversation` can be issued against it — the command would fail (or
// worse, conjure a phantom aggregate). So BOTH kinds of grounding an author
// pins BEFORE the first send are held here, in UI-only state, keyed by the
// provisional conversation id — exactly the way the per-conversation draft is
// held pre-send:
//   • sources — the reference material pinned into Context (a list; introduced
//     as `introduceToContext` on flush).
//   • situation — the audience / learning-context the conversation is grounded
//     to (AT MOST ONE; grounded as `groundConversation` on flush). A later pick
//     REPLACES the pending one (last-write-wins), matching the single-situation
//     semantics of the durable `groundConversation`; pending situations never
//     accumulate.
// On the first send the shell FLUSHES the entry (every source ref as an
// `introduceToContext`, the situation as a `groundConversation`) against the
// now-durable anchor and drops the entry; closing a nascent tab without sending
// simply discards it (nothing was written server-side, so there is nothing to
// clean up).
//
// Discipline (pure core, impure shell): the impure shell (`AppShellHost`) holds
// the map in ONE `useState`; these total, fresh-value transitions are the only
// writers. Absent key ⇒ no pending grounding; an entry with no sources AND no
// situation is dropped so the map stays small. SHORTCUT (§6.3): not persisted —
// a reloaded nascent tab reopens with no pending grounding, same as its draft.

import type {
    ConversationId,
    ResourceRef,
} from "../components/Navigation/types.js";

// One nascent conversation's held grounding: its pending sources (a list) and
// its pending situation (at most one context id, `undefined` when unset).
export type PendingGroundingEntry = {
    readonly sources: readonly ResourceRef[];
    readonly situationContextId: string | undefined;
};

export type PendingGrounding = ReadonlyMap<
    ConversationId,
    PendingGroundingEntry
>;

export const emptyPendingGrounding: PendingGrounding = new Map();

const emptyEntry: PendingGroundingEntry = {
    sources: [],
    situationContextId: undefined,
};

const entryFor = (
    map: PendingGrounding,
    conversationId: ConversationId,
): PendingGroundingEntry => map.get(conversationId) ?? emptyEntry;

const isEmptyEntry = (entry: PendingGroundingEntry): boolean =>
    entry.sources.length === 0 && entry.situationContextId === undefined;

// Set (or drop, when emptied) one conversation's entry — the single writer that
// keeps the drop-empty invariant in one place.
const withEntry = (
    map: PendingGrounding,
    conversationId: ConversationId,
    entry: PendingGroundingEntry,
): PendingGrounding => {
    const next = new Map(map);
    if (isEmptyEntry(entry)) next.delete(conversationId);
    else next.set(conversationId, entry);
    return next;
};

// The source refs pending grounding for a conversation — the empty list when
// none.
export const pendingGroundingFor = (
    map: PendingGrounding,
    conversationId: ConversationId,
): readonly ResourceRef[] => map.get(conversationId)?.sources ?? [];

// The situation (learning-context id) pending grounding for a conversation —
// `undefined` when none.
export const pendingSituationFor = (
    map: PendingGrounding,
    conversationId: ConversationId,
): string | undefined => map.get(conversationId)?.situationContextId;

// Add one source ref to a conversation's pending grounding (dedup by id — a
// source already pending is a no-op, never a duplicate).
export const addPendingGrounding = (
    map: PendingGrounding,
    conversationId: ConversationId,
    ref: ResourceRef,
): PendingGrounding => {
    const entry = entryFor(map, conversationId);
    if (entry.sources.some((r) => r.id === ref.id)) return map;
    return withEntry(map, conversationId, {
        ...entry,
        sources: [...entry.sources, ref],
    });
};

// Remove one source ref (by id) from a conversation's pending grounding; an
// entry left with no sources and no situation is dropped. A no-op (same
// reference) when the ref was not pending.
export const removePendingGrounding = (
    map: PendingGrounding,
    conversationId: ConversationId,
    resourceId: string,
): PendingGrounding => {
    const entry = map.get(conversationId);
    if (entry === undefined || !entry.sources.some((r) => r.id === resourceId))
        return map;
    return withEntry(map, conversationId, {
        ...entry,
        sources: entry.sources.filter((r) => r.id !== resourceId),
    });
};

// Clear a conversation's pending SOURCES (the sidecard's grounding "clear all")
// — leaves the pending situation intact (audience is a separate intent), and
// drops the whole entry only when no situation remains. A no-op (same
// reference) when there were no pending sources.
export const clearPendingGrounding = (
    map: PendingGrounding,
    conversationId: ConversationId,
): PendingGrounding => {
    const entry = map.get(conversationId);
    if (entry === undefined || entry.sources.length === 0) return map;
    return withEntry(map, conversationId, { ...entry, sources: [] });
};

// Set (last-write-wins) a conversation's pending situation — a later pick
// REPLACES any earlier one, matching the durable single-situation semantics. A
// no-op (same reference) when the same context is already pending.
export const setPendingSituation = (
    map: PendingGrounding,
    conversationId: ConversationId,
    contextId: string,
): PendingGrounding => {
    const entry = entryFor(map, conversationId);
    if (entry.situationContextId === contextId) return map;
    return withEntry(map, conversationId, {
        ...entry,
        situationContextId: contextId,
    });
};

// Clear a conversation's pending situation (the sidecard's unground on a
// nascent tab); an entry left with no situation and no sources is dropped. A
// no-op (same reference) when none was pending.
export const clearPendingSituation = (
    map: PendingGrounding,
    conversationId: ConversationId,
): PendingGrounding => {
    const entry = map.get(conversationId);
    if (entry === undefined || entry.situationContextId === undefined)
        return map;
    return withEntry(map, conversationId, {
        ...entry,
        situationContextId: undefined,
    });
};
